import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tool", "dropZone"]
  static values = {
    id: Number,
    placeUrl: String,
    removeUrl: String
  }

  connect() {
    // console.log("Scripture Dig controller connected")
    // console.log("Place URL:", this.placeUrlValue)
    // console.log("Remove URL:", this.removeUrlValue)
    // console.log("Drop zones:", this.dropZoneTargets.length)

    this.draggedToolId = null
    this.draggedToolName = null
    this.draggedToolElement = null
    this.touchStartPos = null
    this.draggedElement = null
    this.isDragging = false
    this.highlightedTargetId = null

    window.addEventListener('scripture-dig:highlight-target', this.boundHighlightTarget)
    window.addEventListener('scripture-dig:unhighlight-target', this.boundUnhighlightTarget)
  }

  disconnect() {
    if (this.dragTimeout) {
      clearTimeout(this.dragTimeout)
    }

    if (this.draggedElement) {
      this.draggedElement.remove()
    }

    this.removeAllHighlights()

    document.body.style.overflow = ""
    document.body.style.touchAction = ""

    window.removeEventListener('scripture-dig:highlight-target', this.boundHighlightTarget)
    window.removeEventListener('scripture-dig:unhighlight-target', this.boundUnhighlightTarget)
  }

  get boundHighlightTarget() {
    if (!this._boundHighlightTarget) {
      this._boundHighlightTarget = (event) => this.highlightTarget(event)
    }
    return this._boundHighlightTarget
  }

  get boundUnhighlightTarget() {
    if (!this._boundUnhighlightTarget) {
      this._boundUnhighlightTarget = (event) => this.unhighlightTarget(event)
    }
    return this._boundUnhighlightTarget
  }

  // FIXED: Compare UUIDs as strings, not integers
  highlightTarget(event) {
    const targetId = event.detail?.targetId
    if (targetId === undefined) {
      // console.log("No targetId in event detail")
      return
    }

    // console.log("Highlighting target:", targetId)
    this.removeAllHighlights()

    const dropZone = this.dropZoneTargets.find(
      zone => zone.dataset.targetId === String(targetId)
    )

    if (dropZone) {
      dropZone.classList.add("ring-2", "ring-custom-blue", "bg-custom-blue-light", "rounded")
      this.highlightedTargetId = targetId
      // console.log("Target highlighted successfully")
    } else {
      // console.log("Drop zone not found for id:", targetId)
    }
  }

  unhighlightTarget(event) {
    // console.log("Unhighlighting target")
    this.removeAllHighlights()
    this.highlightedTargetId = null
  }

  removeAllHighlights() {
    this.dropZoneTargets.forEach(zone => {
      zone.classList.remove("ring-2", "ring-blue-500", "bg-custom-blue-light", "rounded")
    })
  }

  // Desktop drag handlers
  handleToolDragStart(event) {
    // console.log("Drag started (desktop)")
    const toolElement = event.currentTarget
    this.draggedToolId = toolElement.dataset.toolId
    this.draggedToolName = toolElement.dataset.toolName
    this.draggedToolElement = toolElement

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.draggedToolId)
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const dropZone = event.currentTarget
    // FIXED: Compare as strings
    if (dropZone.dataset.targetId !== String(this.highlightedTargetId)) {
      dropZone.classList.add("bg-primary-light", "rounded", "px-1")
    }
  }

  handleDragLeave(event) {
    const dropZone = event.currentTarget
    dropZone.classList.remove("bg-primary-light", "px-1")
    // FIXED: Compare as strings
    if (dropZone.dataset.targetId !== String(this.highlightedTargetId)) {
      dropZone.classList.remove("rounded")
    }
  }

  handleDrop(event) {
    event.preventDefault()
    // console.log("Drop event triggered (desktop)")

    const dropZone = event.currentTarget
    dropZone.classList.remove("bg-primary-light", "px-1")
    // FIXED: Compare as strings
    if (dropZone.dataset.targetId !== String(this.highlightedTargetId)) {
      dropZone.classList.remove("rounded")
    }

    // FIXED: Get targetId as string (UUID)
    const targetId = dropZone.dataset.targetId
    const toolId = event.dataTransfer.getData("text/plain") || this.draggedToolId

    // console.log("Dropping tool:", toolId, "on target:", targetId)

    if (toolId && targetId !== undefined) {
      this.placeTool(toolId, this.draggedToolName, targetId, this.draggedToolElement)
    }

    this.draggedToolId = null
    this.draggedToolName = null
    this.draggedToolElement = null
  }

  // Mobile touch handlers
  handleToolTouchStart(event) {
    // console.log("Tool touch started (mobile)")

    if (event.touches.length > 1) {
      // console.log("Multiple touches, ignoring")
      return
    }

    const touch = event.touches[0]
    const toolElement = event.currentTarget

    this.draggedToolId = toolElement.dataset.toolId
    this.draggedToolName = toolElement.dataset.toolName
    this.draggedToolElement = toolElement
    this.touchStartPos = { x: touch.clientX, y: touch.clientY }
    this.isDragging = false

    // console.log("Touch start on tool:", this.draggedToolName)

    this.dragTimeout = setTimeout(() => {
      // console.log("Drag timeout reached")
      if (this.touchStartPos) {
        this.isDragging = true
        this.startDragging(toolElement, touch)
      }
    }, 200)
  }

  startDragging(toolElement, touch) {
    // console.log("Starting drag mode for tool:", this.draggedToolName)

    this.draggedElement = toolElement.cloneNode(true)
    this.draggedElement.classList.add("fixed", "opacity-75", "pointer-events-none", "z-50")
    this.draggedElement.style.width = `${toolElement.offsetWidth}px`
    this.draggedElement.style.transition = "none"
    document.body.appendChild(this.draggedElement)

    this.updateDraggedElementPosition(touch.clientX, touch.clientY)

    document.body.style.overflow = "hidden"
    document.body.style.touchAction = "none"
  }

  handleTouchMove(event) {
    if (!this.draggedToolId) return

    const touch = event.touches[0]
    const deltaY = Math.abs(touch.clientY - this.touchStartPos.y)
    const deltaX = Math.abs(touch.clientX - this.touchStartPos.x)

    if (!this.isDragging && this.dragTimeout) {
      if (deltaX > deltaY && deltaX > 15) {
        // console.log("Horizontal scroll detected, canceling drag")
        clearTimeout(this.dragTimeout)
        this.draggedToolId = null
        this.draggedToolName = null
        this.draggedToolElement = null
        this.touchStartPos = null
        return
      }

      if (deltaY > 20) {
        // console.log("Vertical movement detected, starting drag immediately")
        clearTimeout(this.dragTimeout)
        const toolElement = document.querySelector(`[data-tool-id="${this.draggedToolId}"]`)
        if (toolElement && !this.isDragging) {
          this.isDragging = true
          this.startDragging(toolElement, touch)
        }
      }
    }

    if (this.isDragging && this.draggedElement) {
      event.preventDefault()
      event.stopPropagation()

      this.updateDraggedElementPosition(touch.clientX, touch.clientY)

      const elementUnderTouch = document.elementFromPoint(touch.clientX, touch.clientY)
      const dropZone = elementUnderTouch?.closest('[data-scripture-dig-target="dropZone"]')

      this.dropZoneTargets.forEach(zone => {
        // FIXED: Compare as strings
        if (zone.dataset.targetId === String(this.highlightedTargetId)) {
          return
        }

        if (zone === dropZone) {
          zone.classList.add("bg-primary-light", "rounded", "px-1")
        } else {
          zone.classList.remove("bg-primary-light", "rounded", "px-1")
        }
      })
    }
  }

  handleTouchEnd(event) {
    // console.log("Touch ended", { isDragging: this.isDragging, draggedToolId: this.draggedToolId })

    if (this.dragTimeout) {
      clearTimeout(this.dragTimeout)
      this.dragTimeout = null
    }

    document.body.style.overflow = ""
    document.body.style.touchAction = ""

    if (!this.isDragging || !this.draggedElement) {
      // console.log("Touch ended but wasn't dragging")
      this.draggedToolId = null
      this.draggedToolName = null
      this.draggedToolElement = null
      this.isDragging = false
      this.touchStartPos = null
      return
    }

    const touch = event.changedTouches[0]
    const elementUnderTouch = document.elementFromPoint(touch.clientX, touch.clientY)
    const dropZone = elementUnderTouch?.closest('[data-scripture-dig-target="dropZone"]')

    // console.log("Drop zone found:", !!dropZone)

    this.draggedElement.remove()
    this.draggedElement = null

    // FIXED: Compare as strings
    this.dropZoneTargets.forEach(zone => {
      if (zone.dataset.targetId !== String(this.highlightedTargetId)) {
        zone.classList.remove("bg-primary-light", "rounded", "px-1")
      } else {
        zone.classList.remove("bg-primary-light", "px-1")
      }
    })

    if (dropZone && this.draggedToolId) {
      // FIXED: Get targetId as string (UUID)
      const targetId = dropZone.dataset.targetId
      // console.log("Placing tool:", this.draggedToolId, "on target:", targetId)
      this.placeTool(
        this.draggedToolId,
        this.draggedToolName,
        targetId,
        this.draggedToolElement
      )
    }

    this.draggedToolId = null
    this.draggedToolName = null
    this.draggedToolElement = null
    this.isDragging = false
    this.touchStartPos = null

    event.preventDefault()
  }

  handleTouchCancel(event) {
    // console.log("Touch cancelled")

    if (this.dragTimeout) {
      clearTimeout(this.dragTimeout)
      this.dragTimeout = null
    }

    document.body.style.overflow = ""
    document.body.style.touchAction = ""

    if (this.draggedElement) {
      this.draggedElement.remove()
      this.draggedElement = null
    }

    // FIXED: Compare as strings
    this.dropZoneTargets.forEach(zone => {
      if (zone.dataset.targetId !== String(this.highlightedTargetId)) {
        zone.classList.remove("bg-primary-light", "rounded", "px-1")
      } else {
        zone.classList.remove("bg-primary-light", "px-1")
      }
    })

    this.draggedToolId = null
    this.draggedToolName = null
    this.draggedToolElement = null
    this.isDragging = false
    this.touchStartPos = null
  }

  updateDraggedElementPosition(x, y) {
    if (!this.draggedElement) return

    this.draggedElement.style.left = `${x - 50}px`
    this.draggedElement.style.top = `${y - 20}px`
  }

  closeDraftPane(event) {
    event.preventDefault()

    const draftId = event.currentTarget.dataset.draftId
    if (draftId) {
      document.getElementById(draftId)?.remove()
    }

    const pane = document.getElementById('interact_pane_content')
    if (pane) {
      pane.innerHTML = ''
    }

    this.removeAllHighlights()
  }

  draftId(toolId, targetId) {
    return `draft-tool-placement-${targetId}-${toolId}`.replace(/[^a-zA-Z0-9_-]/g, '-')
  }

  insertDraftToolPlacement(toolId, targetId, toolElement) {
    const container = document.getElementById(`tool-placements-${targetId}`)
    if (!container) return null

    const draftId = this.draftId(toolId, targetId)
    document.getElementById(draftId)?.remove()

    const icon = toolElement?.querySelector('svg')?.cloneNode(true)
    const badge = document.createElement('span')
    badge.id = draftId
    badge.dataset.broadcastDedupFrameId = draftId
    badge.dataset.broadcastDedupLoaded = 'true'
    badge.className = `inline-flex items-center gap-1 p-1 rounded-full whitespace-nowrap
      -mt-[2px] cursor-pointer ${toolElement?.dataset.toolCss || ''}`

    if (icon) {
      badge.appendChild(icon)
    }

    container.appendChild(badge)

    return draftId
  }

  // Send target_id as string (UUID), no verse_number
  async placeTool(toolId, toolName, targetId, toolElement) {
    // console.log("=== placeTool called ===")
    // console.log("Tool ID:", toolId)
    // console.log("Tool name:", toolName)
    // console.log("Target ID:", targetId)
    // console.log("URL:", this.placeUrlValue)

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const draftId = this.insertDraftToolPlacement(toolId, targetId, toolElement)

    const payload = {
      tool_id: toolId,
      tool_name: toolName,
      target_id: targetId,
      draft_id: draftId
    }
    // console.log("Payload:", payload)

    try {
      const response = await fetch(this.placeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(payload)
      })

      // console.log("Response status:", response.status)

      if (response.ok) {
        const html = await response.text()
        // console.log("Turbo stream HTML received, length:", html.length)
        Turbo.renderStreamMessage(html)
      } else {
        if (draftId) {
          document.getElementById(draftId)?.remove()
        }

        const text = await response.text()
        console.error('Failed to place tool. Status:', response.status)
        console.error('Response:', text)
      }
    } catch (error) {
      if (draftId) {
        document.getElementById(draftId)?.remove()
      }

      console.error('Error placing tool:', error)
    }
  }

  // FIXED: Send target_id as string
  async removeTool(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const placementId = button.dataset.placementId
    const toolId = button.dataset.toolId
    const toolName = button.dataset.toolName
    const targetId = button.dataset.targetId

    // console.log('=== removeTool called ===')
    // console.log('Placement ID:', placementId)
    // console.log('Tool ID:', toolId)
    // console.log('Tool name:', toolName)
    // console.log('Target ID:', targetId)

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    try {
      const response = await fetch(this.removeUrlValue, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          placement_id: placementId,
          tool_id: toolId,
          tool_name: toolName,
          target_id: targetId  // Send as string
        })
      })

      // console.log('Remove response status:', response.status)

      if (response.ok) {
        const html = await response.text()
        // console.log('Turbo stream HTML received for removal')
        Turbo.renderStreamMessage(html)
      } else {
        console.error('Failed to remove tool:', response.status)
        const text = await response.text()
        console.error('Response:', text)
      }
    } catch (error) {
      console.error('Error removing tool:', error)
    }
  }
}
